// frontend/src/lib/api/areasService.ts
import { BaseApiService } from './baseService';
import { ApiResponse } from '../httpClient';

export interface ServiceArea {
  area_id: number;
  provider_id: number;
  pincode: string;
  city: string;
  state: string;
  district?: string;
  region?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface AreasFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  provider_id?: number;
  state?: string;
  city?: string;
  district?: string;
  provider_only?: boolean;
}

export interface CreateAreaData {
  pincode: string;
  city: string;
  state: string;
  district?: string;
  region?: string;
  is_active?: boolean;
}

export interface UpdateAreaData {
  pincode?: string;
  city?: string;
  state?: string;
  district?: string;
  region?: string;
  is_active?: boolean;
}

export interface AreasResponse {
  areas: ServiceArea[];
  pagination?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    has_more: boolean;
  };
  stats?: {
    total: number;
    active: number;
    inactive: number;
  };
}

export interface PincodeValidationResponse {
  valid: boolean;
  pincode: string;
  city?: string;
  state?: string;
  district?: string;
  region?: string;
  error?: string;
}

export interface PincodeSuggestionsResponse {
  suggestions: Array<{
    pincode: string;
    city: string;
    state: string;
    district: string;
    region: string;
  }>;
}

export interface AreaCoverage {
  total_pincodes: number;
  covered_pincodes: number;
  coverage_percentage: number;
  uncovered_areas: Array<{
    pincode: string;
    city: string;
    state: string;
    demand_score: number;
  }>;
}

class AreasService extends BaseApiService {
  constructor() {
    super({
      baseEndpoint: '/areas',
      timeout: 10000,
    });
  }

  async getAreas(filters: AreasFilters = {}): Promise<ApiResponse<AreasResponse>> {
    return this.get('', { params: filters });
  }

  async getAreaById(id: number): Promise<ApiResponse<{ area: ServiceArea }>> {
    return this.get(`/${id}`);
  }

  async createArea(data: CreateAreaData): Promise<ApiResponse<{ area: ServiceArea }>> {
    return this.post('', data);
  }

  async updateArea(id: number, data: UpdateAreaData): Promise<ApiResponse<{ area: ServiceArea }>> {
    return this.put(`/${id}`, data);
  }

  async deleteArea(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/${id}`);
  }

  async toggleAreaStatus(id: number): Promise<ApiResponse<{ area: ServiceArea }>> {
    return this.post(`/${id}/toggle-status`);
  }

  async bulkUpdateAreas(areaIds: number[], updates: Partial<UpdateAreaData>): Promise<ApiResponse<{ updated_count: number }>> {
    return this.post('/bulk-update', { area_ids: areaIds, ...updates });
  }

  async bulkDeleteAreas(areaIds: number[]): Promise<ApiResponse<{ deleted_count: number }>> {
    return this.post('/bulk-delete', { area_ids: areaIds });
  }

  async validatePincode(pincode: string): Promise<ApiResponse<PincodeValidationResponse>> {
    return this.post('/validate-pincode', { pincode });
  }

  async getPincodeSuggestions(query: string): Promise<ApiResponse<PincodeSuggestionsResponse>> {
    return this.post('/pincode-suggestions', { query });
  }

  async getAreaCoverage(filters: AreasFilters = {}): Promise<ApiResponse<AreaCoverage>> {
    return this.get('/coverage', { params: filters });
  }

  async checkAreaAvailability(pincode: string, serviceType?: string): Promise<ApiResponse<{
    available: boolean;
    providers_count: number;
    nearest_areas?: ServiceArea[];
  }>> {
    return this.post('/check-availability', { pincode, service_type: serviceType });
  }
}

export const areasService = new AreasService();